import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Inject, Injectable, inject } from "@angular/core";
import type { Guid, IIdentityProfilDataInfo, IResponse } from "@kiraah-shared/common";
import { IdentityProfilDataRequest, LvfAppModule, LvfOperationType, LvfRequestSource } from "@kiraah-shared/common";
import { ENVIRONMENT } from "@kiraah-shared/environment";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { MOBILE_GATEWAY_URL } from "../../auth/mobile-auth.config";
import { MobileAuthStore } from "../../state/mobile-auth.store";

/**
 * Charge contexte métier {@link IdentityProfilData}, aligné SPA
 * ({@link EntityProfilService.switchActiveApp} / `_load`).
 */
@Injectable({ providedIn: "root" })
export class MobileEntityProfilApiClient {
  private readonly http = inject(HttpClient);
  private readonly authStore = inject(MobileAuthStore);
  private readonly environmentInfo = inject(ENVIRONMENT);

  private readonly entityProfilsUrl: string;

  constructor(@Inject(MOBILE_GATEWAY_URL) gatewayUrl: string) {
    this.entityProfilsUrl = `${gatewayUrl}/api/core/entityprofils`;
  }

  /**
   * @param tenantAppUid Application ciblée (`TenantAppUid` du body — comme le web).
   */
  loadIdentityProfilData(tenantAppUid: string | undefined): Observable<IIdentityProfilDataInfo | null> {
    const completeUrl = `${this.entityProfilsUrl}/_load`;
    const profilData = this.authStore.authResult()?.ProfilData;
    const identity = profilData?.Identity;

    const requestData = IdentityProfilDataRequest.createInstance({
      TenantAppUid: tenantAppUid as Guid | undefined,
      IdentityUid: identity?.Uid as Guid | undefined,
      Email: identity?.Email ?? undefined,
      Environment: this.environmentInfo.Environment,
    });

    return this.http
      .post<IResponse<IIdentityProfilDataInfo>>(completeUrl, requestData, {
        headers: this.loadHeaders(String(tenantAppUid ?? "").trim()),
      })
      .pipe(map((response) => (response?.IsSuccessful && response.Data ? response.Data : null)));
  }

  /** En-tête `X-APP-UID`: l’application demandée lors du `_load` (pas l’état précédent). */
  private loadHeaders(targetAppUid: string): HttpHeaders {
    const identityUid = `${this.authStore.authResult()?.ProfilData?.Identity?.Uid ?? ""}`;
    const appUid = targetAppUid !== "" ? targetAppUid : "";
    return new HttpHeaders({
      "Content-Type": "application/json",
      "X-CONNECTED-IDENTITY-UID": identityUid,
      "X-USER-TOKEN-UID": "",
      "X-APP-UID": appUid,
      "X-APP-MODULE-UID": `${LvfAppModule.ENTITY_PROFIL}`,
      "X-REQUEST-SRC": `${LvfRequestSource.MOBILE}`,
      "X-OPERATION-TYPE": `${LvfOperationType.READ}`,
      "X-WEB-APP-URL": `${this.environmentInfo.WebAppUrl ?? ""}`,
    });
  }
}
